/**
 * The experimental panels that a deployment can switch off: the rule flows, behind
 * EXPERIMENTAL_RULE_FLOWS, and AI Search, behind EXPERIMENTAL_AI_SEARCH. A panel switched off is
 * no panel id (`isPanelId`), so nothing opens it: not the rail, the launcher, a URL, a restored
 * layout or the chat.
 */

import { fetchExperimentalAiSearch, fetchExperimentalRuleFlows } from '@/api/runtimeConfig';
import type { PanelId } from '@/types/panels';

/** The panels of one switch, and whether the deployment turns them on. */
interface PanelSwitch {
    panels: readonly PanelId[];
    fetchOn: () => Promise<boolean>;
}

const SWITCHES: readonly PanelSwitch[] = [
    { panels: ['rule_flow', 'rule_flow_details'], fetchOn: fetchExperimentalRuleFlows },
    { panels: ['ai_search'], fetchOn: fetchExperimentalAiSearch },
];

/** Each panel is on until the switches are read: the switches are on by default. */
export class PanelSwitches {
    private off: ReadonlySet<unknown> = new Set();

    private readonly switches: readonly PanelSwitch[];

    constructor(switches: readonly PanelSwitch[] = SWITCHES) {
        this.switches = switches;
    }

    /** Reads the switches; the page calls it once, as it starts. */
    async load(): Promise<void> {
        const on = await Promise.all(this.switches.map(async (entry) => entry.fetchOn()));
        this.off = new Set(this.switches.flatMap(
            (entry, index) => (on[index] === true ? [] : entry.panels)));
    }

    isOff(id: unknown): boolean {
        return this.off.has(id);
    }
}

export const panelSwitches = new PanelSwitches();
